/**
 * PowersTable Component
 * Shared responsive table for displaying bond powers
 * Used by both requesting (read-only) and posting (with actions) sides
 */

import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatCurrency, formatDate } from './formatUtils';
import { OathSpinner } from '../../../shared/OathUI';

const PowersTable = ({
  powers = [],
  showActions = false,
  onEdit,
  onDelete,
  loading = false,
  emptyMessage = 'No powers available.',
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <OathSpinner size="md" />
      </div>
    );
  }

  if (powers.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
        {emptyMessage}
      </p>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Power #</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Bond Amount</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Offense</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Case #</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Execution Date</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Court Date</th>
              {showActions && (
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {powers.map((power, index) => (
              <tr key={power.transferBondId || power.transferBondID || power.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">
                  {power.powerNumber || '-'}
                </td>
                <td className="px-4 py-3 text-gray-900 dark:text-white">
                  {formatCurrency(power.bondAmount)}
                </td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  {power.offense || '-'}
                </td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  {power.caseNumber || '-'}
                </td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  {formatDate(power.executionDate)}
                </td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                  {formatDate(power.courtDate)}
                </td>
                {showActions && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onEdit?.(power)}
                        className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete?.(power)}
                        className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {powers.map((power, index) => (
          <div
            key={power.transferBondId || power.transferBondID || power.id || index}
            className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 bg-white dark:bg-gray-800"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Power #{power.powerNumber || '-'}
              </span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {formatCurrency(power.bondAmount)}
              </span>
            </div>
            {power.offense && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Offense:</span> {power.offense}
              </p>
            )}
            {power.caseNumber && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                <span className="font-medium">Case #:</span> {power.caseNumber}
              </p>
            )}
            <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Exec: {formatDate(power.executionDate)}</span>
              <span>Court: {formatDate(power.courtDate)}</span>
            </div>
            {showActions && (
              <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => onEdit?.(power)}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <PencilIcon className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => onDelete?.(power)}
                  className="flex items-center gap-1 text-xs text-red-600 hover:underline"
                >
                  <TrashIcon className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default PowersTable;
